import type {
  EmailSignInResponse,
  EmailSignInStartResponse,
  Locale,
  SessionResponse,
} from '@mentorivo/shared';

import { api } from '@/lib/api';

/**
 * Oturum — artık gerçek.
 *
 * ---------------------------------------------------------------------------
 * Jetonu neden burada tutmuyoruz
 * ---------------------------------------------------------------------------
 * Access ve refresh jetonları API tarafından `HttpOnly` çerez olarak yazılıyor;
 * bu dosya onları GÖREMİYOR ve görmemeli. Sayfaya sızan tek bir script'in
 * `localStorage`'dan jeton okuyabilmesi, XSS'i oturum çalmaya çeviriyor.
 *
 * Bunun bedeli: "giriş yapmış mıyım" sorusunu istemci tarafında çerez okuyarak
 * cevaplayamıyoruz. İki yerde çözülüyor:
 *   1. Rota koruması `src/proxy.ts` içinde — sunucuda çalışıyor, HttpOnly
 *      çerezi görebiliyor.
 *   2. Arayüzün ihtiyacı olan kullanıcı bilgisi `getSession()` ile alınıyor.
 *
 * ---------------------------------------------------------------------------
 * Etkin workspace çerezi
 * ---------------------------------------------------------------------------
 * `mentorivo_ws` bilerek HttpOnly DEĞİL: hangi kiracının açık olduğu gizli bir
 * bilgi değil ve switcher'ın ilk boyamada seçili kalemi göstermesi gerekiyor.
 */

export const WORKSPACE_COOKIE = 'mentorivo_ws';

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

/** Tarayıcının bildiği etkin workspace. Doğrulama sunucuda yapılıyor. */
export function activeWorkspaceId(): string | null {
  return readCookie(WORKSPACE_COOKIE);
}

/**
 * PAROLASIZ GİRİŞ (10 Eki 2026) — adrese kod + bağlantı.
 *
 * Giriş ve kayıt AYNI uç: hesap yoksa doğrulamada açılıyor. `name` yalnız
 * kayıt ekranından gelir (hesap varsa sunucu yok sayar). `next` bağlantı
 * BAŞKA bir cihazda açılsa da dönülecek yer — sunucuda saklanıp doğrulama
 * yanıtıyla geri geliyor.
 */
export function startEmailSignIn(input: {
  email: string;
  locale: Locale;
  name?: string;
  next?: string;
}): Promise<EmailSignInStartResponse> {
  return api.post<EmailSignInStartResponse>('/auth/email/start', input);
}

/** Formdaki altı haneyle giriş. Başarıda oturum çerezleri API'den yazılıyor. */
export function verifyEmailCode(email: string, code: string): Promise<EmailSignInResponse> {
  return api.post<EmailSignInResponse>('/auth/email/verify', { email, code });
}

/** Postadaki bağlantıyla giriş — `/auth/link` sayfası `#` sonrasını gönderiyor. */
export function verifyEmailLink(email: string, token: string): Promise<EmailSignInResponse> {
  return api.post<EmailSignInResponse>('/auth/email/link', { email, token });
}

export function getSession(): Promise<SessionResponse> {
  return api.get<SessionResponse>('/auth/session');
}

/**
 * Çıkış. Hata yutuluyor: jeton zaten ölmüşse API 4xx dönebilir ama
 * kullanıcının niyeti belli — çerezler her hâlükârda temizlenmeli ve
 * arayüz giriş sayfasına gitmeli.
 */
export async function signOut(): Promise<void> {
  await api.post('/auth/logout').catch(() => undefined);
}

export function verifyEmail(token: string): Promise<void> {
  return api.post('/auth/verify-email', { token });
}

export function resendVerification(email: string): Promise<{ sent: boolean }> {
  return api.post<{ sent: boolean }>('/auth/resend-verification', { email });
}

/**
 * E-posta değişiminin İKİNCİ aşaması — postadaki bağlantının tükettiği jeton.
 *
 * `/auth/*` değil `/me/*` altında ama burada duruyor, `lib/settings-api.ts`te
 * değil: bu dosyanın topladığı şey "postadan gelen herkese açık bağlantının
 * çağırdığı uç" (`verifyEmail`, `verifyEmailLink` ile aynı şekil). Çağıran sayfa
 * oturumsuz (`/[locale]/settings/email-change`) ve `settings-api` panel
 * bağlamına ait.
 *
 * Birinci aşama (`requestEmailChange`) orada: o oturum ve mevcut adrese
 * giden teyit kodunu istiyor.
 */
export function confirmEmailChange(token: string): Promise<{ email: string }> {
  return api.post<{ email: string }>('/me/email-change/confirm', { token });
}
